import type { PblMeetingPurpose } from '@/types/pbl';

export const PBL_MEETING_DURATION_MINUTES = 45;

export const getPblMeetingEndDateTime = (startsAt: string): string | null => {
  const match = startsAt.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return null;

  const [, year, month, day, hour, minute] = match;
  const start = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)));
  if (
    start.getUTCFullYear() !== Number(year) ||
    start.getUTCMonth() + 1 !== Number(month) ||
    start.getUTCDate() !== Number(day) ||
    start.getUTCHours() !== Number(hour) ||
    start.getUTCMinutes() !== Number(minute)
  ) return null;

  return new Date(start.getTime() + PBL_MEETING_DURATION_MINUTES * 60_000).toISOString().slice(0, 16);
};

export const isPblMeetingWithinSameDay = (startsAt: string): boolean => {
  const endsAt = getPblMeetingEndDateTime(startsAt);
  return Boolean(endsAt && endsAt.slice(0, 10) === startsAt.slice(0, 10));
};

type PblMeetingTranslator = (key: string, options?: Record<string, unknown>) => string;

const purposeLabels: Record<string, string> = {
  oc_intake: 'OC intake meeting',
  lawyer_consultation: 'Lawyer consultation meeting',
};

const purposeLabelKeys: Record<string, string> = {
  oc_intake: 'pbl.caseDetailDocuments.meeting.purpose.ocIntake',
  lawyer_consultation: 'pbl.caseDetailDocuments.meeting.purpose.lawyerConsultation',
};

const meetingEditButtonLabels: Record<string, string> = {
  oc_intake: 'Edit intake meeting time',
  lawyer_consultation: 'Reschedule confirmed consultation',
};

const meetingEditButtonLabelKeys: Record<string, string> = {
  oc_intake: 'pbl.caseDetailDocuments.meeting.actions.editIntakeMeetingTime',
  lawyer_consultation: 'pbl.caseDetailDocuments.meeting.actions.reschedule',
};

const meetingEditModalTitles: Record<string, string> = {
  oc_intake: 'Edit OC intake meeting time',
  lawyer_consultation: 'Reschedule confirmed consultation',
};

const meetingEditModalTitleKeys: Record<string, string> = {
  oc_intake: 'pbl.caseDetailDocuments.modals.meetingEdit.editOcIntakeMeetingTime',
  lawyer_consultation: 'pbl.caseDetailDocuments.modals.meetingEdit.reschedule',
};

const meetingEditModalNotices: Record<string, string> = {
  oc_intake: 'Update the intake meeting time used for the preliminary consultation and transcript.',
  lawyer_consultation: 'Update the confirmed consultation meeting time. To ask the lawyer for new options, return the case to the timeslot proposal flow.',
};

const meetingEditModalNoticeKeys: Record<string, string> = {
  oc_intake: 'pbl.caseDetailDocuments.modals.meetingEdit.ocIntakeNotice',
  lawyer_consultation: 'pbl.caseDetailDocuments.modals.meetingEdit.lawyerConsultationNotice',
};

const translateWithFallback = (t: PblMeetingTranslator | undefined, key: string, fallback: string) =>
  t ? t(key, { defaultValue: fallback }) : fallback;

const titleCaseWords = (value: string) =>
  value
    .split('_')
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');

export const getPblMeetingPurposeLabel = (purpose?: PblMeetingPurpose | null, t?: PblMeetingTranslator) => {
  if (!purpose) return undefined;

  const fallback = purposeLabels[purpose] || titleCaseWords(purpose);
  const key = purposeLabelKeys[purpose];

  return key ? translateWithFallback(t, key, fallback) : fallback;
};

export const getPblMeetingEditButtonLabel = (purpose?: PblMeetingPurpose | null, t?: PblMeetingTranslator) => {
  const defaultLabel = translateWithFallback(
    t,
    'pbl.caseDetailDocuments.meeting.actions.rescheduleMeeting',
    'Reschedule meeting'
  );

  if (!purpose) return defaultLabel;

  const fallback = meetingEditButtonLabels[purpose] || 'Reschedule meeting';
  const key = meetingEditButtonLabelKeys[purpose];

  return key ? translateWithFallback(t, key, fallback) : defaultLabel;
};

export const getPblMeetingEditModalTitle = (purpose?: PblMeetingPurpose | null, t?: PblMeetingTranslator) => {
  const defaultTitle = translateWithFallback(
    t,
    'pbl.caseDetailDocuments.modals.meetingEdit.rescheduleMeeting',
    'Reschedule meeting'
  );

  if (!purpose) return defaultTitle;

  const fallback = meetingEditModalTitles[purpose] || 'Reschedule meeting';
  const key = meetingEditModalTitleKeys[purpose];

  return key ? translateWithFallback(t, key, fallback) : defaultTitle;
};

export const getPblMeetingEditModalNotice = (purpose?: PblMeetingPurpose | null, t?: PblMeetingTranslator) => {
  const defaultNotice = translateWithFallback(
    t,
    'pbl.caseDetailDocuments.modals.meetingEdit.defaultNotice',
    'Update the confirmed meeting time. This does not create a new set of proposed times.'
  );

  if (!purpose) return defaultNotice;

  const fallback =
    meetingEditModalNotices[purpose] ||
    'Update the confirmed meeting time. This does not create a new set of proposed times.';
  const key = meetingEditModalNoticeKeys[purpose];

  return key ? translateWithFallback(t, key, fallback) : defaultNotice;
};
